// Demo Adapter
// P1.1: Read-only adapter that returns pre-seeded Apex Home Remodeling demo data

import type { IStorageAdapter } from "../storage-adapter"
import type { Memory } from "@/lib/constants/memory"

/**
 * Demo memories for Apex Home Remodeling
 * These match the data seeded in Supabase
 */
export const DEMO_MEMORIES: Memory[] = [
  {
    id: "d4e5f6a7-b8c9-0123-def0-456789012345",
    field: "business_name",
    label: "Business Name",
    value: "Apex Home Remodeling",
    section: "identity",
    sectionLabel: "Identity",
    category: "business",
    categoryLabel: "Business",
    timestamp: Date.now(),
    isArchived: false,
  },
  {
    id: "e5f6a7b8-c9d0-1234-ef01-567890123456",
    field: "industry",
    label: "Industry",
    value: "Home Services - Residential Remodeling & Renovation",
    section: "identity",
    sectionLabel: "Industry",
    category: "market",
    categoryLabel: "Market",
    timestamp: Date.now(),
    isArchived: false,
  },
  {
    id: "f6a7b8c9-d0e1-2345-f012-678901234567",
    field: "products_services",
    label: "Products/Services",
    value:
      "Full-service home remodeling including kitchen renovations, bathroom remodels, basement finishing, whole-home renovations, and outdoor living spaces. We specialize in mid-to-high-end residential projects with an average project value of $75K-$250K.",
    section: "offerings",
    sectionLabel: "Offerings",
    category: "offerings",
    categoryLabel: "Offerings",
    timestamp: Date.now(),
    isArchived: false,
  },
  {
    id: "a7b8c9d0-e1f2-3456-0123-789012345678",
    field: "target_market",
    label: "Target Market",
    value:
      "Affluent homeowners aged 35-65 in the Denver metro area, Boulder, and Fort Collins. Typically dual-income households with home values $500K+ who prioritize quality craftsmanship over lowest price. Many are planning to age-in-place or preparing homes for resale.",
    section: "audience",
    sectionLabel: "Audience",
    category: "audience",
    categoryLabel: "Audience",
    timestamp: Date.now(),
    isArchived: false,
  },
  {
    id: "b8c9d0e1-f2a3-4567-1234-890123456789",
    field: "goals",
    label: "Current Goals",
    value:
      "Scale from $3.2M to $5M revenue in the next 18 months. Open a fourth location in Colorado Springs. Reduce project timeline by 15% through better scheduling and subcontractor management. Increase repeat/referral business from 40% to 60%.",
    section: "strategy",
    sectionLabel: "Strategy",
    category: "strategy",
    categoryLabel: "Strategy",
    timestamp: Date.now(),
    isArchived: false,
  },
]

/**
 * DemoAdapter provides read-only access to demo memories.
 * Used when demo mode is active to showcase the Business Brain with real data.
 */
export class DemoAdapter implements IStorageAdapter {
  readonly name = "demo"
  readonly isReadOnly = true

  private listeners = new Set<() => void>()

  async getMemories(includeArchived = false): Promise<Memory[]> {
    return includeArchived ? DEMO_MEMORIES : DEMO_MEMORIES.filter((m) => !m.isArchived)
  }

  async saveMemory(_memory: {
    field: string
    label: string
    value: string
    section: string
  }): Promise<Memory> {
    console.warn("[DemoAdapter] Cannot save memory in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to save memories.")
  }

  async updateMemory(_id: string, _updates: Partial<Memory>): Promise<void> {
    console.warn("[DemoAdapter] Cannot update memory in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to update memories.")
  }

  async deleteMemory(_id: string): Promise<void> {
    console.warn("[DemoAdapter] Cannot delete memory in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to delete memories.")
  }

  async archiveMemory(_id: string): Promise<void> {
    console.warn("[DemoAdapter] Cannot archive memory in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to archive memories.")
  }

  async restoreMemory(_id: string): Promise<void> {
    console.warn("[DemoAdapter] Cannot restore memory in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to restore memories.")
  }

  async getMemoryByField(fieldId: string): Promise<Memory | undefined> {
    return DEMO_MEMORIES.find((m) => m.field === fieldId && !m.isArchived)
  }

  async getKeyDetailsCompleted(): Promise<number> {
    // Demo has all 5 core fields completed
    return 5
  }

  async clearAll(): Promise<void> {
    console.warn("[DemoAdapter] Cannot clear memories in demo mode")
    throw new Error("Demo mode is read-only. Disable demo mode to clear memories.")
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }
}

// Singleton instance
let demoAdapter: DemoAdapter | null = null

export function getDemoAdapter(): DemoAdapter {
  if (!demoAdapter) {
    demoAdapter = new DemoAdapter()
  }
  return demoAdapter
}
